/**
 * Alta y edición de usuario. Reescrito en JSX legible el 2026-10-07: sin «rol»
 * ni «acceso a áreas»; lo que cada usuario puede hacer (páginas, acciones y
 * unidades) se marca aparte, en Usuarios → Permisos.
 */
import {
  rec_Componente_qD_113_68 as NombreSede,
  rec_Kk as nombrePosicion,
  rec_Lo as claseEntrada,
  rec_MarcaCarga as MarcaCarga,
  rec_Modal as Modal,
  rec_React as React,
  rec_Ro as clasePrimario,
  rec_api as api,
  rec_ii as IconoCarga,
  rec_zo as claseSecundario,
} from "../../runtime/index.jsx";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useState = (...a) => React.useState(...a);

export function FormularioUsuario({ editar, onClose, onGuardado }) {
  const [datos, setDatos] = useState({
    nombre: editar?.nombre ?? ``,
    apellido: editar?.apellido ?? ``,
    usuario: editar?.usuario ?? ``,
    correo: editar?.correo ?? ``,
    // Se guarda con el código de país (51…); acá se escriben sólo los 9 dígitos.
    celular: (editar?.celular ?? ``).replace(/\D/g, ``).slice(-9),
    activo: !editar || editar.activo,
    contrasena: ``,
    correoExterno: false,
  });
  const [disponible, setDisponible] = useState(null);
  const [error, setError] = useState(``);
  const [guardando, setGuardando] = useState(false);
  const [trabaja, setTrabaja] = useState([]);

  useEffect(() => {
    if (!editar) return;
    api(`/accesos-unidad/trabaja?usuario=${editar.id}`).then((t) => setTrabaja(t ?? [])).catch(() => setTrabaja([]));
  }, [editar]);

  const campo = (k) => (v) => setDatos((d) => ({ ...d, [k]: v }));

  // Al dejar de escribir el usuario: ¿está libre? Si no, dos sugerencias.
  const usuarioCambio = !editar || datos.usuario.trim().toLowerCase() !== (editar.usuario ?? ``).toLowerCase();
  useEffect(() => {
    if (!usuarioCambio || !datos.usuario.trim()) return setDisponible(null);
    setDisponible({ revisando: true });
    const t = setTimeout(() => {
      const q = new URLSearchParams({ usuario: datos.usuario.trim(), nombre: datos.nombre, apellido: datos.apellido });
      api(`/usuarios/disponible?${q}`, { sinCache: true }).then(setDisponible).catch(() => setDisponible(null));
    }, 600);
    return () => clearTimeout(t);
  }, [datos.usuario, usuarioCambio]);
  const celularOk = /^9\d{8}$/.test(datos.celular);

  async function guardar(ev) {
    ev.preventDefault();
    if (guardando) return;
    if (!celularOk) return setError(`El celular tiene 9 dígitos y empieza con 9.`);
    if (disponible && disponible.disponible === false) return setError(disponible.motivo ?? `Ese usuario ya está ocupado`);
    setGuardando(true);
    setError(``);
    const cuerpo = { ...datos, rol: editar?.rol ?? `usuario` };
    if (!cuerpo.contrasena) delete cuerpo.contrasena;
    try {
      if (editar) await api(`/usuarios/${editar.id}`, { method: `PUT`, body: JSON.stringify(cuerpo) });
      else await api(`/usuarios`, { method: `POST`, body: JSON.stringify(cuerpo) });
      onGuardado();
    } catch (e) {
      setError(e.message);
      setGuardando(false);
    }
  }

  return (
    <Modal titulo={editar ? `Editar ${editar.usuario}` : `Nuevo usuario`} onClose={onClose}>
      <form onSubmit={guardar} className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <input className={claseEntrada} placeholder="Nombre" value={datos.nombre} onChange={(e) => campo(`nombre`)(e.target.value)} required autoFocus />
          <input className={claseEntrada} placeholder="Apellido" value={datos.apellido} onChange={(e) => campo(`apellido`)(e.target.value)} required />
        </div>
        <div className="flex flex-col gap-1">
          <div className="relative">
            <input className={`${claseEntrada} w-full pr-9`} placeholder="Usuario (para iniciar sesión)" value={datos.usuario} autoComplete="off"
              onChange={(e) => campo(`usuario`)(e.target.value.toLowerCase().replace(/\s+/g, ``))} required />
            {disponible && !disponible.revisando && (
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-base font-bold ${disponible.disponible ? `text-(--success)` : `text-(--error)`}`}
                aria-label={disponible.disponible ? `Disponible` : `Ocupado`}>{disponible.disponible ? `✓` : `✗`}</span>
            )}
          </div>
          {disponible?.revisando && <p className="text-xs text-(--text)">Revisando…</p>}
          {disponible && disponible.disponible === true && <p className="text-xs text-(--success)">Disponible</p>}
          {disponible && disponible.disponible === false && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-(--error)">{disponible.motivo}. Escribe otro{disponible.sugerencias?.length ? `, por ejemplo:` : `.`}</span>
              {(disponible.sugerencias ?? []).map((sug) => (
                <button key={sug} type="button" onClick={() => campo(`usuario`)(sug)}
                  className="rounded-full border border-(--border) px-2 py-0.5 text-(--text-h) hover:border-(--accent) hover:text-(--accent)">{sug}</button>
              ))}
            </div>
          )}
        </div>
        <input className={claseEntrada} type="email" placeholder="Correo" value={datos.correo} onChange={(e) => campo(`correo`)(e.target.value.trim())} required />
        <MarcaCarga checked={datos.correoExterno} onChange={campo(`correoExterno`)}>Proveedor con correo externo</MarcaCarga>
        <div className="flex flex-col gap-1">
          <input className={claseEntrada} inputMode="numeric" placeholder="Celular (9 dígitos)" aria-label="Celular" value={datos.celular} maxLength={9}
            onChange={(e) => campo(`celular`)(e.target.value.replace(/\D/g, ``).slice(0, 9))} required />
          <p className={`text-xs ${datos.celular && !celularOk ? `text-(--error)` : `text-(--text)`}`}>
            {datos.celular && !celularOk ? `Son 9 dígitos y empieza con 9.` : `Para los avisos por WhatsApp. Ejemplo: 987654321.`}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <input className={claseEntrada} type="password" value={datos.contrasena} onChange={(e) => campo(`contrasena`)(e.target.value)} required={!editar}
            placeholder={editar ? `Nueva contraseña (dejar vacío para no cambiarla)` : `Contraseña inicial (ej. el DNI)`} />
          <p className="text-[11px] text-(--text)">
            Mínimo 6 caracteres. Es temporal — puede ser el DNI: el login además pide un código por correo. Cuando la persona la cambie desde su perfil se le exige mayúscula y número.
          </p>
        </div>
        <MarcaCarga checked={datos.activo} onChange={campo(`activo`)}>Activo</MarcaCarga>

        {editar && (
          <div className="rounded-2xl border border-(--border) p-3">
            <p className="text-xs font-medium text-(--text-h)">
              Trabaja en <span className="font-normal text-(--text)">(según Organización)</span>
            </p>
            {trabaja.length === 0 ? (
              <p className="mt-1 text-[11px] text-(--text)">No está en ninguna unidad. Se suma desde Organización.</p>
            ) : (
              <ul className="mt-1.5 flex flex-col gap-1">
                {trabaja.map((t) => (
                  <li key={t.unidadId} className="flex flex-wrap items-center gap-1.5 text-sm text-(--text-h)">
                    {t.unidadNombre}
                    <NombreSede nombre={t.sedeNombre} />
                    <span className="text-[11px] text-(--text)">· {nombrePosicion[t.posicion] ?? t.posicion}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <p className="text-[11px] text-(--text)">
          Lo que puede ver y hacer (páginas, acciones y unidades) se marca en <strong>Permisos</strong>, en la lista de usuarios.
          {!editar && ` Un usuario nuevo entra sin permisos.`}
        </p>

        {error && <p className="text-sm text-(--error)">{error}</p>}
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={claseSecundario}>Cancelar</button>
          <button type="submit" disabled={guardando || disponible?.disponible === false || disponible?.revisando} className={clasePrimario}>
            {guardando && <IconoCarga className="mr-1 inline h-4 w-4 animate-spin" />}
            {editar ? `Guardar` : `Crear`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
